export const VTO_LANGS = ['it', 'ro', 'en', 'de', 'fr', 'es', 'tr'] as const;
export type VtoLang = typeof VTO_LANGS[number];

type CopyTree = Record<string, any>;

const it = {
  meta: {
    lang: 'it'
  },
  common: {
    brand: 'BeautyIQ',
    vtoTitle: 'Virtual Try-On',
    backToBeautyIQ: '← Torna a BeautyIQ',
    cancel: '← Annulla',
    retry: 'Riprova',
    ok: 'OK',
    demo: 'Demo',
    error: 'Errore'
  },
  intensity: {
    soft: 'Naturale',
    glam: 'Glam',
    bold: 'Intenso'
  },
  processing: {
    detecting: {
      title: 'Analizzo il viso',
      detail: 'Cerco proporzioni, occhi, labbra e orientamento del volto.',
      progress: 18
    },
    calibrating: {
      title: 'Calibro luce e pelle',
      detail: 'Regolo intensità e texture in base al selfie.',
      progress: 36
    },
    preparing: {
      title: 'Preparo il motore ad alta definizione',
      detail: 'Carico il parser del viso per maschere più precise. Solo al primo utilizzo.',
      progress: 50
    },
    adapting: {
      title: 'Adatto il look al tuo volto',
      detail: 'Preparo le aree trucco per pelle, occhi, guance e labbra.',
      progress: 62
    },
    rendering: {
      title: 'Applico il makeup',
      detail: 'Sto componendo il look completo mantenendo i dettagli naturali.',
      progress: 82
    },
    refining: {
      title: 'Rifinisco sfumature e texture',
      detail: 'Finalizzo il risultato per renderlo più naturale.',
      progress: 94
    },
    waitHint: 'Qualche secondo di attesa serve a rendere il risultato più credibile sul tuo selfie.'
  },
  onboarding: {
    subtitle: 'Una foto guidata, un look realistico sul tuo viso.',
    eyebrow: 'Selfie guidato',
    title: '1 foto, 3 controlli automatici',
    body: 'Inizi con un look pronto, poi puoi cambiare intensità e palette dopo il render. Il motore legge volto, luce e pelle prima di applicare labbra, guance e occhi.',
    selectedLook: 'Look scelto',
    initialLook: 'Look iniziale',
    cta: 'Scatta foto guidata',
    privacy: 'La foto resta sul tuo dispositivo: tutta l’elaborazione avviene in locale, nessuna immagine viene inviata a server.',
    demoTitle: 'Prova senza selfie',
    demoBody: 'Usa una foto demo per vedere subito il motore.',
    checks: [
      {
        label: 'Luce uniforme',
        detail: 'Finestra davanti, niente sole diretto o ombre nette.'
      },
      {
        label: 'Viso frontale',
        detail: 'Guarda la camera, capelli ordinati e lontani dal viso, sfondo semplice.'
      },
      {
        label: 'Pelle pulita',
        detail: 'Bocca rilassata e poco makeup precedente nelle zone da provare.'
      }
    ]
  },
  access: {
    remaining: (count: number) => `${count} prove VTO gratuite rimaste`,
    ended: 'Prove gratuite terminate',
    note: "Ogni look generato su una foto consuma una prova. Il trial sblocca l'uso completo."
  },
  captureHeader: {
    subtitle: 'Inquadra il tuo viso, poi scatta.'
  },
  capture: {
    labels: {
      good: 'Scatto pulito',
      adjust: 'Quasi pronta',
      blocked: 'Da correggere',
      warming: 'Controllo viso',
      unavailable: 'Controllo non disponibile',
      idle: 'Controllo viso'
    },
    errors: {
      cameraUnavailable: 'Camera non disponibile',
      cameraUnsupported: 'Fotocamera non supportata da questo browser',
      videoNotReady: 'Camera avviata, ma il video non è pronto',
      captureFailed: 'Impossibile catturare',
      unreadableImage: 'Immagine non leggibile',
      testFaceFailed: 'Test face non caricabile'
    },
    scan: {
      startMessage: 'Guarda dritto verso la camera.',
      startDetail: 'Ti avviso quando luce, distanza e posa sono buone.',
      centerFace: 'Porta il viso al centro.',
      centerFaceDetail: 'Fronte e guance libere, luce uniforme.',
      fixBeforeShot: 'Correggi prima dello scatto per evitare un risultato impreciso.',
      readyMessage: 'Ora puoi scattare.',
      readyDetail: 'Viso, distanza e luce sono adatti al VTO.',
      improveMessage: 'Manca poco: migliora luce o posa.',
      improveDetail: 'Puoi scattare, ma una foto più pulita rende meglio.',
      unavailableMessage: 'Controllo live non disponibile.',
      unavailableDetail: 'Puoi comunque scattare: la verifica completa avverrà dopo.'
    },
    camera: {
      enable: 'Continua',
      starting: 'Avvio fotocamera...',
      errorTitle: 'Errore',
      deniedTitle: 'Fotocamera bloccata',
      deniedBody: "Consenti l'accesso nelle impostazioni del browser, oppure carica una foto.",
      ok: 'OK',
      snap: 'Scatta foto',
      upload: '...oppure carica una foto',
      privacy: 'La foto resta sul tuo dispositivo. Niente viene inviato a server.'
    }
  },
  paywall: {
    title: (limit: number) => `Hai usato le ${limit} prove gratuite`,
    body: 'Sblocca BeautyIQ per continuare a provare look, esportare la Look Card e usare il Virtual Try-On senza questo limite.',
    loading: 'Apro checkout...',
    cta: 'Sblocca uso completo',
    checkoutError: 'Checkout non disponibile in questo momento. Riprova tra poco.',
    privacy: 'Limite anonimo per questo browser. Non salviamo selfie o immagini per applicarlo.'
  },
  noFace: {
    title: 'Volto non rilevato',
    fallback: 'Prova con una foto frontale, ben illuminata, bocca rilassata e chiusa.'
  },
  result: {
    refineLook: 'Rifinisco il look',
    viewLabel: 'Vista risultato',
    previewToggle: 'Toggle prima e dopo',
    compareLabel: 'Confronto prima/dopo',
    after: 'Dopo',
    before: 'Prima',
    compare: 'Confronto',
    intensity: 'Intensità trucco',
    hair: 'Capelli',
    hairNone: 'Originale',
    hairForYou: 'Consigliati per il tuo sottotono',
    hairNote: 'le prove non schiariscono molto i capelli scuri',
    hairNoteDark: 'su capelli chiari o grigi rende come riflesso, non li scurisce',
    subtone: 'Sottotono',
    warm: 'caldo',
    cool: 'freddo',
    neutral: 'neutro',
    opacityAdjusted: 'opacità regolata automaticamente',
    light: 'Luce',
    lightAdjusted: 'intensità calibrata sul selfie',
    skin: 'Pelle',
    skinAdjusted: 'pigmenti calibrati',
    fallbackNotice: 'Ho ottimizzato il rendering per questa foto mantenendo il risultato visibile.',
    quality: 'Qualità',
    engine: 'Motore',
    time: 'Tempo',
    privacySummary: (engine: string) => `${engine}. La foto resta nel browser.`,
    simulationNote: 'Simulazione con sfumatura verticale, definizione cigliare e intensità sopracciglia. Tono, profondità pelle e luce regolano automaticamente l’opacità. La resa finale varia con la luce reale: verifica i colori in negozio.',
    betterResult: 'Per un risultato ancora più preciso',
    qualityWarningGeneric: 'La foto non è ideale: ho ammorbidito intensità e texture per mantenere un risultato naturale.',
    premiumButton: 'Render finale',
    premiumRendering: 'Creo render finale...',
    premiumPrivacy: "Il render finale invia temporaneamente l'immagine composta al server BeautyIQ per la rifinitura.",
    share: 'Condividi il look',
    sharing: 'Preparo immagine...',
    newPhoto: '↻ Nuova foto'
  },
  qualitySummary: {
    semanticHigh: 'Alta precisione',
    semanticBalanced: 'Bilanciata',
    evaluating: 'In valutazione',
    excellent: 'Ottima',
    stable: 'Stabile',
    optimized: 'Ottimizzata',
    localMesh: 'Face mesh locale',
    localMeshCachedParser: 'Face mesh locale, parser in cache',
    advancedParsing: 'Face parsing avanzato'
  },
  skinDepth: {
    'very-light': 'molto chiara',
    light: 'chiara',
    medium: 'media',
    tan: 'ambrata',
    deep: 'profonda'
  },
  skinChroma: {
    muted: 'soft',
    balanced: 'bilanciata',
    rich: 'satura'
  },
  components: {
    lipstick: 'Rossetto',
    blush: 'Blush',
    eyeshadow: 'Ombretto'
  },
  kit: {
    title: 'Il tuo kit',
    notes: 'Note sui colori',
    lipstick: 'Rossetto',
    blush: 'Blush',
    eyeshadow: 'Ombretto'
  },
  crossBrand: {
    title: 'Match cross-brand',
    body: 'Nuance equivalenti da più brand, solo match con scarto colore reale (ΔE ≤ 5), ordinate per vicinanza.',
    buy: 'Acquista',
    viewLook: 'Vedi questo look',
    quality: {
      perfect: 'Perfetto',
      excellent: 'Ottimo',
      good: 'Buono',
      approximate: 'Approssimativo'
    }
  },
  mirror: {
    title: 'Specchio live',
    hint: 'Muovi il viso: il look ti segue. Premi Cattura per il risultato e i match.',
    capture: 'Cattura',
    searching: 'Cerco il tuo viso…'
  },
  lookCard: {
    title: 'Look Card BeautyIQ',
    export: 'Esporta',
    preparing: 'Preparo...',
    code: 'Codice look',
    palette: 'Palette',
    temperature: 'Temperatura',
    contrast: 'Contrasto',
    chroma: 'Chroma',
    skinFit: 'Fit pelle',
    counterBrief: 'Brief da banco',
    skin: 'Pelle',
    lips: 'Labbra',
    eyes: 'Occhi',
    cheeks: 'Guance',
    avoid: 'Evita',
    controls: 'Controlli resa',
    canvasFooter: 'Porta questa card da Kiko, Douglas, Sephora o MAC e chiedi un equivalente.'
  },
  rationale: {
    title: 'Perché funziona',
    forWho: 'Per chi',
    when: 'Quando',
    avoid: 'Da evitare',
    storeHint: 'Porta questo look in negozio. La tua make-up artist ti aiuterà a trovare i prodotti giusti per la tua pelle.'
  },
  beautyPlayers: {
    title: 'Porta il look da',
    body: 'Mostra la Look Card e chiedi prodotti equivalenti per palette, finish e intensità.'
  },
  premium: {
    progress: [
      'Preparo il render finale...',
      'Invio il risultato al motore premium...',
      'Rifinisco luce, pelle e texture...',
      'Controllo la resa finale...',
      'Sto finalizzando il render...'
    ],
    readyMs: (ms: number) => `Render finale pronto in ${ms} ms.`,
    ready: 'Render finale pronto: ho applicato la rifinitura server.',
    localReady: 'Render locale pronto: server premium non disponibile, risultato mantenuto.',
    queued: 'Render in coda: sto aspettando il risultato premium...',
    unavailable: 'Render finale non disponibile ora. Il risultato locale resta utilizzabile.',
    alt: (styleName: string) => `${styleName} render finale BeautyIQ`,
    genericAlt: 'Render finale BeautyIQ'
  },
  quality: {
    blockers: {
      pose: 'Prova con il viso un po’ più frontale: così il risultato sarà molto più preciso.',
      mouth: 'Prova con la bocca più rilassata e quasi chiusa per un bordo labbra più preciso.',
      distance: 'Avvicina un po’ il viso alla camera: così il try-on avrà più dettaglio.'
    },
    warnings: {
      tilted: 'Viso leggermente inclinato: ho reso più delicati occhi e guance per mantenere il risultato naturale.',
      poseLimit: 'La posa è un po’ al limite: il look è stato mantenuto più morbido per restare credibile.',
      mouthOpen: 'Bocca leggermente aperta: ho reso il rossetto un po’ più morbido per evitare sbavature visive.',
      faceFar: 'Volto un po’ lontano: occhi e guance sono stati tenuti più leggeri per conservare precisione.',
      lowLight: 'Luce morbida o bassa: ho tenuto i colori più soft per un risultato più pulito.',
      unevenLight: 'La luce sul viso non è uniforme: ho reso il look un po’ più delicato per mantenerlo naturale.',
      softPhoto: 'La foto è un po’ morbida: ho privilegiato precisione e naturalezza rispetto all’intensità.',
      notSharp: 'La foto non è perfettamente nitida: il look è stato ottimizzato per restare pulito.'
    }
  },
  lighting: {
    unmeasurable: 'Luce non misurabile con precisione: ho mantenuto una calibrazione neutra.',
    lowExposure: 'Foto poco luminosa: ho aumentato leggermente pigmento e ridotto shimmer eccessivo.',
    highExposure: 'Foto molto luminosa: ho ridotto pigmento e riflessi per evitare effetto adesivo.',
    harshContrast: 'Contrasto forte sul volto: ho ammorbidito guance, occhi e finish lucidi.'
  },
  skinTone: {
    unmeasurable: 'Tono pelle non misurabile: ho mantenuto una calibrazione colore neutra.',
    reducedSample: 'Campione pelle ridotto: la taratura colore resta conservativa.'
  },
  loadDemoError: 'Impossibile caricare la foto demo',
  detectionError: 'Rilevamento volto fallito'
} as const;

export type VtoCopy = typeof it;

function mergeCopy<T extends CopyTree>(base: T, override: CopyTree): T {
  const output: CopyTree = Array.isArray(base) ? [...base] : { ...base };
  Object.entries(override).forEach(([key, value]) => {
    const current = output[key];
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      typeof value !== 'function' &&
      current &&
      typeof current === 'object' &&
      !Array.isArray(current) &&
      typeof current !== 'function'
    ) {
      output[key] = mergeCopy(current, value);
    } else {
      output[key] = value;
    }
  });
  return output as T;
}

const en = mergeCopy(it, {
    meta: { lang: 'en' },
    common: {
      backToBeautyIQ: '← Back to BeautyIQ',
      cancel: '← Cancel',
      retry: 'Try again',
      error: 'Error'
    },
    intensity: { soft: 'Natural', glam: 'Glam', bold: 'Bold' },
    processing: {
      detecting: { title: 'Analyzing your face', detail: 'Reading proportions, eyes, lips and face angle.' },
      calibrating: { title: 'Calibrating light and skin', detail: 'Adjusting intensity and texture from your selfie.' },
      preparing: { title: 'Preparing the high-definition engine', detail: 'Loading the face parser for more precise makeup areas. First use only.' },
      adapting: { title: 'Adapting the look to your face', detail: 'Preparing makeup areas for skin, eyes, cheeks and lips.' },
      rendering: { title: 'Applying makeup', detail: 'Composing the full look while preserving natural detail.' },
      refining: { title: 'Refining blends and texture', detail: 'Finishing the result so it feels more natural.' },
      waitHint: 'A few seconds help make the result more believable on your selfie.'
    },
    onboarding: {
      subtitle: 'One guided photo, one realistic look on your face.',
      eyebrow: 'Guided selfie',
      title: '1 photo, 3 automatic checks',
      body: 'Start with a ready look, then change intensity and palette after rendering. The engine reads face, light and skin before applying lips, cheeks and eyes.',
      selectedLook: 'Selected look',
      initialLook: 'Starting look',
      cta: 'Take guided photo',
      privacy: 'The photo stays on your device: all processing happens locally, no image is ever sent to a server.',
      demoTitle: 'Try without a selfie',
      demoBody: 'Use a demo photo to see the engine immediately.',
      checks: [
        { label: 'Even light', detail: 'Window in front, no direct sun or harsh shadows.' },
        { label: 'Frontal face', detail: 'Look at the camera, hair tidy and away from your face, plain background.' },
        { label: 'Clean skin', detail: 'Relaxed mouth and little previous makeup on the areas to try.' }
      ]
    },
    access: {
      remaining: (count: number) => `${count} free VTO tries left`,
      ended: 'Free tries finished',
      note: 'Each look generated on a photo uses one try. The trial unlocks full use.'
    },
    captureHeader: { subtitle: 'Frame your face, then shoot.' },
    capture: {
      labels: {
        good: 'Clean shot',
        adjust: 'Almost ready',
        blocked: 'Needs correction',
        warming: 'Face check',
        unavailable: 'Check unavailable',
        idle: 'Face check'
      },
      errors: {
        cameraUnavailable: 'Camera unavailable',
        cameraUnsupported: 'Camera not supported by this browser',
        videoNotReady: 'Camera started, but the video is not ready',
        captureFailed: 'Could not capture',
        unreadableImage: 'Image unreadable',
        testFaceFailed: 'Test face unavailable'
      },
      scan: {
        startMessage: 'Look straight at the camera.',
        startDetail: 'I will tell you when light, distance and pose are good.',
        centerFace: 'Bring your face to the center.',
        centerFaceDetail: 'Forehead and cheeks clear, even light.',
        fixBeforeShot: 'Correct this before shooting to avoid an inaccurate result.',
        readyMessage: 'You can shoot now.',
        readyDetail: 'Face, distance and light are suitable for VTO.',
        improveMessage: 'Almost there: improve light or pose.',
        improveDetail: 'You can shoot, but a cleaner photo looks better.',
        unavailableMessage: 'Live check unavailable.',
        unavailableDetail: 'You can still shoot: the full check will run after.'
      },
      camera: {
        enable: 'Continue',
        starting: 'Starting camera...',
        errorTitle: 'Error',
        deniedTitle: 'Camera blocked',
        deniedBody: 'Allow access in browser settings, or upload a photo.',
        ok: 'OK',
        snap: 'Take photo',
        upload: '...or upload a photo',
        privacy: 'The photo stays on your device. Nothing is sent to a server.'
      }
    },
    paywall: {
      title: (limit: number) => `You used the ${limit} free tries`,
      body: 'Unlock BeautyIQ to keep trying looks, export the Look Card and use Virtual Try-On without this limit.',
      loading: 'Opening checkout...',
      cta: 'Unlock full use',
      checkoutError: 'Checkout is not available right now. Try again shortly.',
      privacy: 'Anonymous limit for this browser. We do not save selfies or images to apply it.'
    },
    noFace: {
      title: 'Face not detected',
      fallback: 'Try a frontal, well-lit photo with a relaxed, almost closed mouth.'
    },
    result: {
      refineLook: 'Refining the look',
      viewLabel: 'Result view',
      compareLabel: 'Before/after comparison',
      after: 'After',
      before: 'Before',
      compare: 'Compare',
      intensity: 'Makeup intensity',
      hair: 'Hair',
      hairNone: 'Original',
      hairForYou: 'Recommended for your undertone',
      hairNote: "try-ons don't lighten dark hair much",
      hairNoteDark: 'on light or grey hair this reads as a tint, it does not darken',
      subtone: 'Undertone',
      warm: 'warm',
      cool: 'cool',
      neutral: 'neutral',
      opacityAdjusted: 'opacity adjusted automatically',
      light: 'Light',
      lightAdjusted: 'intensity calibrated on the selfie',
      skin: 'Skin',
      skinAdjusted: 'pigments calibrated',
      fallbackNotice: 'I optimized rendering for this photo while keeping the result visible.',
      quality: 'Quality',
      engine: 'Engine',
      time: 'Time',
      privacySummary: (engine: string) => `${engine}. The photo stays in the browser.`,
      simulationNote: 'Simulation with vertical blending, lash-line definition and eyebrow intensity. Tone, skin depth and light automatically adjust opacity. Final color varies with real lighting: verify shades in store.',
      betterResult: 'For an even more precise result',
      qualityWarningGeneric: 'The photo is not ideal: I softened intensity and texture to keep the result natural.',
      premiumButton: 'Final render',
      premiumRendering: 'Creating final render...',
      premiumPrivacy: 'Final render temporarily sends the composed image to the BeautyIQ server for refinement.',
      share: 'Share the look',
      sharing: 'Preparing image...',
      newPhoto: '↻ New photo'
    },
    qualitySummary: {
      semanticHigh: 'High precision',
      semanticBalanced: 'Balanced',
      evaluating: 'Evaluating',
      excellent: 'Excellent',
      stable: 'Stable',
      optimized: 'Optimized',
      localMesh: 'Local face mesh',
      localMeshCachedParser: 'Local face mesh, cached parser',
      advancedParsing: 'Advanced face parsing'
    },
    skinDepth: { 'very-light': 'very light', light: 'light', medium: 'medium', tan: 'tan', deep: 'deep' },
    skinChroma: { muted: 'soft', balanced: 'balanced', rich: 'rich' },
    components: { lipstick: 'Lipstick', blush: 'Blush', eyeshadow: 'Eyeshadow' },
    kit: { title: 'Your kit', notes: 'Color notes', lipstick: 'Lipstick', blush: 'Blush', eyeshadow: 'Eyeshadow' },
    crossBrand: {
      title: 'Cross-brand matches',
      body: 'Equivalent shades across brands, only true colour matches (ΔE ≤ 5), ranked by proximity.',
      buy: 'Buy',
      viewLook: 'See this look',
      quality: { perfect: 'Perfect', excellent: 'Excellent', good: 'Good', approximate: 'Approximate' }
    },
    mirror: { title: 'Live mirror', hint: 'Move your face: the look follows. Tap Capture for the result and matches.', capture: 'Capture', searching: 'Looking for your face…' },
    lookCard: {
      export: 'Export',
      preparing: 'Preparing...',
      code: 'Look code',
      temperature: 'Temperature',
      skinFit: 'Skin fit',
      counterBrief: 'Counter brief',
      skin: 'Skin',
      lips: 'Lips',
      eyes: 'Eyes',
      cheeks: 'Cheeks',
      avoid: 'Avoid',
      controls: 'Render checks',
      canvasFooter: 'Bring this card to Kiko, Douglas, Sephora or MAC and ask for an equivalent.'
    },
    rationale: {
      title: 'Why it works',
      forWho: 'For whom',
      when: 'When',
      avoid: 'Avoid',
      storeHint: 'Bring this look in store. Your makeup artist can help find the right products for your skin.'
    },
    beautyPlayers: {
      title: 'Bring the look to',
      body: 'Show the Look Card and ask for equivalent products by palette, finish and intensity.'
    },
    premium: {
      progress: [
        'Preparing final render...',
        'Sending the result to the premium engine...',
        'Refining light, skin and texture...',
        'Checking final output...',
        'Finalizing render...'
      ],
      readyMs: (ms: number) => `Final render ready in ${ms} ms.`,
      ready: 'Final render ready: server refinement applied.',
      localReady: 'Local render ready: premium server unavailable, result kept.',
      queued: 'Render queued: waiting for the premium result...',
      unavailable: 'Final render unavailable right now. The local result is still usable.',
      alt: (styleName: string) => `${styleName} BeautyIQ final render`,
      genericAlt: 'BeautyIQ final render'
    },
    quality: {
      blockers: {
        pose: 'Try facing the camera a bit more directly: the result will be much more precise.',
        mouth: 'Try with a more relaxed, almost closed mouth for a more precise lip edge.',
        distance: 'Bring your face a little closer to the camera: the try-on will have more detail.'
      },
      warnings: {
        tilted: 'Face slightly tilted: I softened eyes and cheeks to keep the result natural.',
        poseLimit: 'The pose is a bit borderline: the look was kept softer to stay believable.',
        mouthOpen: 'Mouth slightly open: I softened the lipstick a little to avoid visible smudges.',
        faceFar: 'Face a bit far away: eyes and cheeks were kept lighter to preserve precision.',
        lowLight: 'Soft or low light: I kept the colors softer for a cleaner result.',
        unevenLight: 'The light on your face is uneven: I made the look a bit more delicate to keep it natural.',
        softPhoto: 'The photo is a bit soft: I prioritized precision and naturalness over intensity.',
        notSharp: 'The photo is not perfectly sharp: the look was optimized to stay clean.'
      }
    },
    lighting: {
      unmeasurable: 'Light could not be measured precisely: I kept a neutral calibration.',
      lowExposure: 'Low-light photo: I slightly increased pigment and reduced excessive shimmer.',
      highExposure: 'Very bright photo: I reduced pigment and shine to avoid a sticker effect.',
      harshContrast: 'Strong contrast on the face: I softened cheeks, eyes and glossy finishes.'
    },
    skinTone: {
      unmeasurable: 'Skin tone could not be measured: I kept a neutral color calibration.',
      reducedSample: 'Small skin sample: color calibration stays conservative.'
    },
    loadDemoError: 'Could not load the demo photo',
    detectionError: 'Face detection failed'
  });

export const VTO_COPY: Record<VtoLang, VtoCopy> = {
  it,
  en,
  ro: mergeCopy(en, {
    meta: { lang: 'ro' },
    common: { backToBeautyIQ: '← Înapoi la BeautyIQ', cancel: '← Anulează', retry: 'Încearcă din nou', error: 'Eroare' },
    intensity: { soft: 'Natural', glam: 'Glam', bold: 'Intens' },
    processing: {
      preparing: { title: 'Pregătesc motorul de înaltă definiție', detail: 'Încarc parserul facial pentru zone de machiaj mai precise. Doar la prima utilizare.' },
    },
    onboarding: {
      subtitle: 'O fotografie ghidată, un look realist pe fața ta.',
      eyebrow: 'Selfie ghidat',
      title: '1 fotografie, 3 verificări automate',
      body: 'Începi cu un look pregătit, apoi poți schimba intensitatea și paleta după randare. Motorul citește fața, lumina și pielea înainte de buze, obraji și ochi.',
      selectedLook: 'Look ales',
      initialLook: 'Look inițial',
      cta: 'Fă fotografia ghidată',
      privacy: 'Fotografia rămâne pe dispozitivul tău: toată procesarea are loc local, nicio imagine nu este trimisă către un server.',
      demoTitle: 'Încearcă fără selfie',
      demoBody: 'Folosește o fotografie demo ca să vezi imediat motorul.',
      checks: [
        { label: 'Lumină uniformă', detail: 'Fereastră în față, fără soare direct sau umbre dure.' },
        { label: 'Față frontală', detail: 'Privește camera, părul aranjat și departe de față, fundal simplu.' },
        { label: 'Ten curat', detail: 'Gură relaxată și cât mai puțin machiaj anterior pe zonele testate.' }
      ]
    },
    captureHeader: { subtitle: 'Încadrează fața, apoi fotografiază.' },
    access: { remaining: (count: number) => `${count} încercări VTO gratuite rămase`, ended: 'Încercările gratuite s-au terminat', note: 'Fiecare look generat pe o fotografie consumă o încercare. Trialul deblochează utilizarea completă.' },
    capture: {
      camera: { enable: 'Continuă', starting: 'Pornesc camera...', errorTitle: 'Eroare', deniedTitle: 'Camera este blocată', deniedBody: 'Permite accesul din setările browserului sau încarcă o fotografie.', ok: 'OK', snap: 'Fă foto', upload: '...sau încarcă o fotografie', privacy: 'Fotografia rămâne pe dispozitiv. Nimic nu este trimis către server.' },
      labels: { good: 'Cadru curat', adjust: 'Aproape gata', blocked: 'De corectat', warming: 'Verific fața', unavailable: 'Verificare indisponibilă', idle: 'Verific fața' },
      errors: {
        cameraUnsupported: 'Camera nu este suportată de acest browser',
        videoNotReady: 'Camera a pornit, dar imaginea video nu este pregătită'
      }
    },
    quality: {
      blockers: {
        pose: 'Încearcă cu fața puțin mai frontală: rezultatul va fi mult mai precis.',
        mouth: 'Încearcă cu gura mai relaxată și aproape închisă pentru un contur al buzelor mai precis.',
        distance: 'Apropie puțin fața de cameră: proba virtuală va avea mai multe detalii.'
      },
      warnings: {
        tilted: 'Față ușor înclinată: am atenuat ochii și obrajii pentru un rezultat natural.',
        poseLimit: 'Poza este puțin la limită: lookul a fost păstrat mai delicat ca să rămână credibil.',
        mouthOpen: 'Gură ușor deschisă: am făcut rujul puțin mai delicat pentru a evita petele vizibile.',
        faceFar: 'Fața este puțin departe: ochii și obrajii au fost păstrați mai discreți pentru precizie.',
        lowLight: 'Lumină slabă sau difuză: am păstrat culorile mai soft pentru un rezultat mai curat.',
        unevenLight: 'Lumina de pe față nu este uniformă: am făcut lookul puțin mai delicat ca să rămână natural.',
        softPhoto: 'Fotografia este puțin neclară: am pus precizia și naturalețea înaintea intensității.',
        notSharp: 'Fotografia nu este perfect clară: lookul a fost optimizat ca să rămână curat.'
      }
    },
    lighting: {
      unmeasurable: 'Lumina nu poate fi măsurată cu precizie: am păstrat o calibrare neutră.',
      lowExposure: 'Fotografie puțin luminoasă: am crescut ușor pigmentul și am redus shimmerul excesiv.',
      highExposure: 'Fotografie foarte luminoasă: am redus pigmentul și strălucirea pentru a evita efectul de autocolant.',
      harshContrast: 'Contrast puternic pe față: am atenuat obrajii, ochii și finisajele lucioase.'
    },
    skinTone: {
      unmeasurable: 'Tonul pielii nu poate fi măsurat: am păstrat o calibrare de culoare neutră.',
      reducedSample: 'Eșantion de piele redus: calibrarea culorii rămâne conservatoare.'
    },
    result: { after: 'După', before: 'Înainte', compare: 'Comparație', intensity: 'Intensitate machiaj', hair: 'Păr', hairNone: 'Original', hairForYou: 'Recomandate pentru subtonul tău', hairNote: 'probele nu deschid mult părul închis', hairNoteDark: 'pe părul deschis sau gri se vede ca un reflex, nu îl închide', premiumButton: 'Render final', share: 'Distribuie lookul', newPhoto: '↻ Fotografie nouă' },
    components: { lipstick: 'Ruj', blush: 'Blush', eyeshadow: 'Fard' },
    kit: { title: 'Kitul tău', notes: 'Note despre culori', lipstick: 'Ruj', blush: 'Blush', eyeshadow: 'Fard' },
    crossBrand: {
      title: 'Match cross-brand',
      body: 'Nuanțe echivalente din mai multe branduri, doar potriviri reale de culoare (ΔE ≤ 5), sortate după apropiere.',
      buy: 'Cumpără',
      viewLook: 'Vezi acest look',
      quality: { perfect: 'Perfect', excellent: 'Excelent', good: 'Bun', approximate: 'Aproximativ' }
    },
    mirror: { title: 'Oglindă live', hint: 'Mișcă fața: lookul te urmează. Apasă Captură pentru rezultat și potriviri.', capture: 'Captură', searching: 'Caut fața ta…' },
    premium: { readyMs: (ms: number) => `Render final gata în ${ms} ms.`, ready: 'Render final gata: am aplicat rafinarea server.', unavailable: 'Renderul final nu este disponibil acum. Rezultatul local rămâne utilizabil.' }
  }),
  de: mergeCopy(en, {
    meta: { lang: 'de' },
    common: { backToBeautyIQ: '← Zurück zu BeautyIQ', cancel: '← Abbrechen', retry: 'Erneut versuchen', error: 'Fehler' },
    intensity: { soft: 'Natürlich', glam: 'Glam', bold: 'Intensiv' },
    processing: {
      preparing: { title: 'Hochauflösende Engine wird vorbereitet', detail: 'Lade den Gesichts-Parser für präzisere Make-up-Bereiche. Nur beim ersten Mal.' },
    },
    onboarding: {
      subtitle: 'Ein geführtes Foto, ein realistischer Look auf deinem Gesicht.',
      eyebrow: 'Geführtes Selfie',
      title: '1 Foto, 3 automatische Checks',
      body: 'Du startest mit einem fertigen Look und kannst nach dem Rendern Intensität und Palette ändern. Die Engine liest Gesicht, Licht und Haut vor Lippen, Wangen und Augen.',
      selectedLook: 'Gewählter Look',
      initialLook: 'Start-Look',
      cta: 'Geführtes Foto aufnehmen',
      privacy: 'Das Foto bleibt auf deinem Gerät: die gesamte Verarbeitung läuft lokal, kein Bild wird an einen Server gesendet.',
      demoTitle: 'Ohne Selfie testen',
      demoBody: 'Nutze ein Demo-Foto, um die Engine sofort zu sehen.',
      checks: [
        { label: 'Gleichmäßiges Licht', detail: 'Fenster vor dir, keine direkte Sonne oder harten Schatten.' },
        { label: 'Frontales Gesicht', detail: 'In die Kamera schauen, Haare ordentlich und weg vom Gesicht, schlichter Hintergrund.' },
        { label: 'Saubere Haut', detail: 'Entspannter Mund und wenig vorhandenes Makeup auf den Testzonen.' }
      ]
    },
    captureHeader: { subtitle: 'Rahme dein Gesicht ein und nimm das Foto auf.' },
    access: { remaining: (count: number) => `${count} kostenlose VTO-Versuche übrig`, ended: 'Kostenlose Versuche beendet', note: 'Jeder auf einem Foto generierte Look verbraucht einen Versuch. Der Trial schaltet die volle Nutzung frei.' },
    capture: {
      camera: { enable: 'Weiter', starting: 'Kamera startet...', errorTitle: 'Fehler', deniedTitle: 'Kamera blockiert', deniedBody: 'Erlaube den Zugriff in den Browser-Einstellungen oder lade ein Foto hoch.', ok: 'OK', snap: 'Foto aufnehmen', upload: '...oder Foto hochladen', privacy: 'Das Foto bleibt auf deinem Gerät. Nichts wird an einen Server gesendet.' },
      labels: { good: 'Sauberes Foto', adjust: 'Fast bereit', blocked: 'Korrigieren', warming: 'Gesichtscheck', unavailable: 'Check nicht verfügbar', idle: 'Gesichtscheck' },
      errors: {
        cameraUnsupported: 'Kamera wird von diesem Browser nicht unterstützt',
        videoNotReady: 'Kamera gestartet, aber das Video ist noch nicht bereit'
      }
    },
    quality: {
      blockers: {
        pose: 'Halte dein Gesicht etwas frontaler zur Kamera: so wird das Ergebnis deutlich präziser.',
        mouth: 'Versuche es mit entspanntem, fast geschlossenem Mund für eine präzisere Lippenkontur.',
        distance: 'Geh mit dem Gesicht etwas näher an die Kamera: so hat das Try-on mehr Details.'
      },
      warnings: {
        tilted: 'Gesicht leicht geneigt: Ich habe Augen und Wangen weicher gestaltet, damit das Ergebnis natürlich bleibt.',
        poseLimit: 'Die Pose ist etwas grenzwertig: Der Look wurde weicher gehalten, um glaubwürdig zu bleiben.',
        mouthOpen: 'Mund leicht geöffnet: Ich habe den Lippenstift etwas weicher gemacht, um sichtbare Ränder zu vermeiden.',
        faceFar: 'Gesicht etwas weit weg: Augen und Wangen wurden leichter gehalten, um die Präzision zu bewahren.',
        lowLight: 'Weiches oder schwaches Licht: Ich habe die Farben softer gehalten für ein saubereres Ergebnis.',
        unevenLight: 'Das Licht auf dem Gesicht ist ungleichmäßig: Ich habe den Look etwas dezenter gemacht, damit er natürlich bleibt.',
        softPhoto: 'Das Foto ist etwas weich: Ich habe Präzision und Natürlichkeit über Intensität gestellt.',
        notSharp: 'Das Foto ist nicht ganz scharf: Der Look wurde optimiert, um sauber zu bleiben.'
      }
    },
    lighting: {
      unmeasurable: 'Licht nicht genau messbar: Ich habe eine neutrale Kalibrierung beibehalten.',
      lowExposure: 'Wenig Licht im Foto: Ich habe das Pigment leicht erhöht und übermäßigen Schimmer reduziert.',
      highExposure: 'Sehr helles Foto: Ich habe Pigment und Glanz reduziert, um einen Sticker-Effekt zu vermeiden.',
      harshContrast: 'Starker Kontrast im Gesicht: Ich habe Wangen, Augen und glänzende Finishes abgemildert.'
    },
    skinTone: {
      unmeasurable: 'Hautton nicht messbar: Ich habe eine neutrale Farbkalibrierung beibehalten.',
      reducedSample: 'Kleine Hautprobe: Die Farbkalibrierung bleibt konservativ.'
    },
    result: { after: 'Nachher', before: 'Vorher', compare: 'Vergleich', intensity: 'Makeup-Intensität', hair: 'Haar', hairNone: 'Original', hairForYou: 'Empfohlen für deinen Unterton', hairNote: 'die Vorschau hellt dunkles Haar kaum auf', hairNoteDark: 'auf hellem oder grauem Haar wirkt es als Tönung, es dunkelt nicht ab', premiumButton: 'Final Render', share: 'Look teilen', newPhoto: '↻ Neues Foto' },
    components: { lipstick: 'Lippenstift', blush: 'Blush', eyeshadow: 'Lidschatten' },
    kit: { title: 'Dein Kit', notes: 'Farbnotizen', lipstick: 'Lippenstift', blush: 'Blush', eyeshadow: 'Lidschatten' },
    crossBrand: {
      title: 'Cross-Brand-Matches',
      body: 'Äquivalente Farbtöne mehrerer Brands, nur echte Farbtreffer (ΔE ≤ 5), nach Ähnlichkeit sortiert.',
      buy: 'Kaufen',
      viewLook: 'Diesen Look ansehen',
      quality: { perfect: 'Perfekt', excellent: 'Sehr gut', good: 'Gut', approximate: 'Ungefähr' }
    },
    mirror: { title: 'Live-Spiegel', hint: 'Bewege dein Gesicht: der Look folgt. Tippe auf Aufnehmen für Ergebnis und Matches.', capture: 'Aufnehmen', searching: 'Suche dein Gesicht…' }
  }),
  fr: mergeCopy(en, {
    meta: { lang: 'fr' },
    common: { backToBeautyIQ: '← Retour à BeautyIQ', cancel: '← Annuler', retry: 'Réessayer', error: 'Erreur' },
    intensity: { soft: 'Naturel', glam: 'Glam', bold: 'Intense' },
    processing: {
      preparing: { title: 'Préparation du moteur haute définition', detail: 'Chargement de l’analyseur de visage pour des zones de maquillage plus précises. Première utilisation uniquement.' },
    },
    onboarding: {
      subtitle: 'Une photo guidée, un look réaliste sur ton visage.',
      eyebrow: 'Selfie guidé',
      title: '1 photo, 3 contrôles automatiques',
      body: 'Tu commences avec un look prêt, puis tu peux changer intensité et palette après le rendu. Le moteur lit visage, lumière et peau avant les lèvres, joues et yeux.',
      selectedLook: 'Look choisi',
      initialLook: 'Look initial',
      cta: 'Prendre la photo guidée',
      privacy: 'La photo reste sur ton appareil : tout le traitement est local, aucune image n’est envoyée à un serveur.',
      demoTitle: 'Essayer sans selfie',
      demoBody: 'Utilise une photo démo pour voir le moteur tout de suite.',
      checks: [
        { label: 'Lumière uniforme', detail: 'Fenêtre en face, pas de soleil direct ni ombres fortes.' },
        { label: 'Visage de face', detail: 'Regarde la caméra, cheveux nets et loin du visage, fond simple.' },
        { label: 'Peau propre', detail: 'Bouche détendue et peu de makeup déjà présent sur les zones à tester.' }
      ]
    },
    captureHeader: { subtitle: 'Cadre ton visage, puis prends la photo.' },
    access: { remaining: (count: number) => `${count} essais VTO gratuits restants`, ended: 'Essais gratuits terminés', note: 'Chaque look généré sur une photo consomme un essai. Le trial débloque l’usage complet.' },
    capture: {
      camera: { enable: 'Continuer', starting: 'Démarrage caméra...', errorTitle: 'Erreur', deniedTitle: 'Caméra bloquée', deniedBody: 'Autorise l’accès dans les réglages du navigateur ou importe une photo.', ok: 'OK', snap: 'Prendre photo', upload: '...ou importer une photo', privacy: 'La photo reste sur ton appareil. Rien n’est envoyé à un serveur.' },
      labels: { good: 'Photo propre', adjust: 'Presque prête', blocked: 'À corriger', warming: 'Contrôle visage', unavailable: 'Contrôle indisponible', idle: 'Contrôle visage' },
      errors: {
        cameraUnsupported: 'Caméra non prise en charge par ce navigateur',
        videoNotReady: 'Caméra démarrée, mais la vidéo n’est pas prête'
      }
    },
    quality: {
      blockers: {
        pose: 'Essaie avec le visage un peu plus de face : le résultat sera bien plus précis.',
        mouth: 'Essaie avec la bouche plus détendue et presque fermée pour un contour des lèvres plus précis.',
        distance: 'Rapproche un peu ton visage de la caméra : l’essayage aura plus de détails.'
      },
      warnings: {
        tilted: 'Visage légèrement incliné : j’ai adouci yeux et joues pour garder un résultat naturel.',
        poseLimit: 'La pose est un peu limite : le look a été gardé plus doux pour rester crédible.',
        mouthOpen: 'Bouche légèrement ouverte : j’ai adouci un peu le rouge à lèvres pour éviter les bavures visibles.',
        faceFar: 'Visage un peu loin : yeux et joues ont été gardés plus légers pour préserver la précision.',
        lowLight: 'Lumière douce ou faible : j’ai gardé des couleurs plus soft pour un résultat plus propre.',
        unevenLight: 'La lumière sur le visage n’est pas uniforme : j’ai rendu le look un peu plus délicat pour qu’il reste naturel.',
        softPhoto: 'La photo est un peu floue : j’ai privilégié précision et naturel plutôt que l’intensité.',
        notSharp: 'La photo n’est pas parfaitement nette : le look a été optimisé pour rester propre.'
      }
    },
    lighting: {
      unmeasurable: 'Lumière non mesurable avec précision : j’ai gardé une calibration neutre.',
      lowExposure: 'Photo peu lumineuse : j’ai légèrement augmenté le pigment et réduit le shimmer excessif.',
      highExposure: 'Photo très lumineuse : j’ai réduit pigment et reflets pour éviter l’effet autocollant.',
      harshContrast: 'Contraste fort sur le visage : j’ai adouci joues, yeux et finis brillants.'
    },
    skinTone: {
      unmeasurable: 'Ton de peau non mesurable : j’ai gardé une calibration couleur neutre.',
      reducedSample: 'Échantillon de peau réduit : la calibration couleur reste prudente.'
    },
    result: { after: 'Après', before: 'Avant', compare: 'Comparer', intensity: 'Intensité makeup', hair: 'Cheveux', hairNone: 'Original', hairForYou: 'Recommandés pour ton sous-ton', hairNote: "l'essayage n'éclaircit pas vraiment les cheveux foncés", hairNoteDark: 'sur cheveux clairs ou gris, agit comme un reflet, ne fonce pas', premiumButton: 'Rendu final', share: 'Partager le look', newPhoto: '↻ Nouvelle photo' },
    components: { lipstick: 'Rouge à lèvres', blush: 'Blush', eyeshadow: 'Fard à paupières' },
    kit: { title: 'Ton kit', notes: 'Notes couleur', lipstick: 'Rouge à lèvres', blush: 'Blush', eyeshadow: 'Fard à paupières' },
    crossBrand: {
      title: 'Matches multi-marques',
      body: 'Teintes équivalentes de plusieurs marques, seulement les vrais matches (ΔE ≤ 5), classées par proximité.',
      buy: 'Acheter',
      viewLook: 'Voir ce look',
      quality: { perfect: 'Parfait', excellent: 'Excellent', good: 'Bon', approximate: 'Approximatif' }
    },
    mirror: { title: 'Miroir live', hint: 'Bouge ton visage : le look suit. Appuie sur Capturer pour le résultat et les matches.', capture: 'Capturer', searching: 'Je cherche ton visage…' }
  }),
  es: mergeCopy(en, {
    meta: { lang: 'es' },
    common: { backToBeautyIQ: '← Volver a BeautyIQ', cancel: '← Cancelar', retry: 'Intentar de nuevo', error: 'Error' },
    intensity: { soft: 'Natural', glam: 'Glam', bold: 'Intenso' },
    processing: {
      preparing: { title: 'Preparando el motor de alta definición', detail: 'Cargando el analizador facial para zonas de maquillaje más precisas. Solo el primer uso.' },
    },
    onboarding: {
      subtitle: 'Una foto guiada, un look realista en tu rostro.',
      eyebrow: 'Selfie guiado',
      title: '1 foto, 3 controles automáticos',
      body: 'Empiezas con un look listo y luego puedes cambiar intensidad y paleta después del render. El motor lee rostro, luz y piel antes de aplicar labios, mejillas y ojos.',
      selectedLook: 'Look elegido',
      initialLook: 'Look inicial',
      cta: 'Tomar foto guiada',
      privacy: 'La foto queda en tu dispositivo: todo el procesamiento es local, ninguna imagen se envía a un servidor.',
      demoTitle: 'Probar sin selfie',
      demoBody: 'Usa una foto demo para ver el motor al instante.',
      checks: [
        { label: 'Luz uniforme', detail: 'Ventana delante, sin sol directo ni sombras duras.' },
        { label: 'Rostro frontal', detail: 'Mira a cámara, pelo ordenado y lejos de la cara, fondo liso.' },
        { label: 'Piel limpia', detail: 'Boca relajada y poco maquillaje previo en las zonas a probar.' }
      ]
    },
    captureHeader: { subtitle: 'Encuadra tu rostro y toma la foto.' },
    access: { remaining: (count: number) => `${count} pruebas VTO gratis restantes`, ended: 'Pruebas gratis terminadas', note: 'Cada look generado en una foto consume una prueba. El trial desbloquea el uso completo.' },
    capture: {
      camera: { enable: 'Continuar', starting: 'Iniciando cámara...', errorTitle: 'Error', deniedTitle: 'Cámara bloqueada', deniedBody: 'Permite el acceso en los ajustes del navegador o sube una foto.', ok: 'OK', snap: 'Tomar foto', upload: '...o subir una foto', privacy: 'La foto queda en tu dispositivo. Nada se envía a un servidor.' },
      labels: { good: 'Foto limpia', adjust: 'Casi lista', blocked: 'Corregir', warming: 'Control facial', unavailable: 'Control no disponible', idle: 'Control facial' },
      errors: {
        cameraUnsupported: 'Cámara no compatible con este navegador',
        videoNotReady: 'Cámara iniciada, pero el vídeo no está listo'
      }
    },
    quality: {
      blockers: {
        pose: 'Prueba con el rostro un poco más de frente: el resultado será mucho más preciso.',
        mouth: 'Prueba con la boca más relajada y casi cerrada para un borde de labios más preciso.',
        distance: 'Acerca un poco el rostro a la cámara: la prueba tendrá más detalle.'
      },
      warnings: {
        tilted: 'Rostro ligeramente inclinado: suavicé ojos y mejillas para mantener el resultado natural.',
        poseLimit: 'La pose está un poco al límite: el look se mantuvo más suave para seguir siendo creíble.',
        mouthOpen: 'Boca ligeramente abierta: suavicé un poco el labial para evitar manchas visibles.',
        faceFar: 'Rostro un poco lejos: ojos y mejillas se mantuvieron más ligeros para conservar precisión.',
        lowLight: 'Luz suave o baja: mantuve los colores más soft para un resultado más limpio.',
        unevenLight: 'La luz en el rostro no es uniforme: hice el look un poco más delicado para mantenerlo natural.',
        softPhoto: 'La foto es un poco borrosa: prioricé precisión y naturalidad sobre la intensidad.',
        notSharp: 'La foto no es perfectamente nítida: el look se optimizó para mantenerse limpio.'
      }
    },
    lighting: {
      unmeasurable: 'Luz no medible con precisión: mantuve una calibración neutra.',
      lowExposure: 'Foto poco luminosa: aumenté ligeramente el pigmento y reduje el brillo excesivo.',
      highExposure: 'Foto muy luminosa: reduje pigmento y reflejos para evitar efecto pegatina.',
      harshContrast: 'Contraste fuerte en el rostro: suavicé mejillas, ojos y acabados brillantes.'
    },
    skinTone: {
      unmeasurable: 'Tono de piel no medible: mantuve una calibración de color neutra.',
      reducedSample: 'Muestra de piel reducida: la calibración de color se mantiene conservadora.'
    },
    result: { after: 'Después', before: 'Antes', compare: 'Comparar', intensity: 'Intensidad makeup', hair: 'Cabello', hairNone: 'Original', hairForYou: 'Recomendados para tu subtono', hairNote: 'la prueba no aclara mucho el pelo oscuro', hairNoteDark: 'en pelo claro o gris actúa como reflejo, no lo oscurece', premiumButton: 'Render final', share: 'Compartir look', newPhoto: '↻ Nueva foto' },
    components: { lipstick: 'Labial', blush: 'Blush', eyeshadow: 'Sombra' },
    kit: { title: 'Tu kit', notes: 'Notas de color', lipstick: 'Labial', blush: 'Blush', eyeshadow: 'Sombra' },
    crossBrand: {
      title: 'Matches entre marcas',
      body: 'Tonos equivalentes de varias marcas, solo coincidencias reales de color (ΔE ≤ 5), ordenados por proximidad.',
      buy: 'Comprar',
      viewLook: 'Ver este look',
      quality: { perfect: 'Perfecto', excellent: 'Excelente', good: 'Bueno', approximate: 'Aproximado' }
    },
    mirror: { title: 'Espejo en vivo', hint: 'Mueve tu cara: el look te sigue. Pulsa Capturar para el resultado y los matches.', capture: 'Capturar', searching: 'Buscando tu cara…' }
  }),
  tr: mergeCopy(en, {
    meta: { lang: 'tr' },
    common: { backToBeautyIQ: '← BeautyIQ’ya dön', cancel: '← İptal', retry: 'Tekrar dene', error: 'Hata' },
    intensity: { soft: 'Doğal', glam: 'Glam', bold: 'Yoğun' },
    processing: {
      detecting: { title: 'Yüzünü analiz ediyorum', detail: 'Oranları, gözleri, dudakları ve yüzün açısını okuyorum.' },
      calibrating: { title: 'Işığı ve cildi kalibre ediyorum', detail: 'Yoğunluğu ve dokuyu selfie’ne göre ayarlıyorum.' },
      preparing: { title: 'Yüksek çözünürlüklü motor hazırlanıyor', detail: 'Daha hassas makyaj bölgeleri için yüz çözümleyici yükleniyor. Yalnızca ilk kullanımda.' },
      adapting: { title: 'Looku yüzüne uyarlıyorum', detail: 'Cilt, göz, yanak ve dudaklar için makyaj bölgelerini hazırlıyorum.' },
      rendering: { title: 'Makyajı uyguluyorum', detail: 'Doğal detayları koruyarak looku bütün olarak oluşturuyorum.' },
      refining: { title: 'Geçişleri ve dokuyu rötuşluyorum', detail: 'Sonucu daha doğal hissettirmesi için son haline getiriyorum.' },
      waitHint: 'Birkaç saniyelik bekleme, sonucun selfie’nde daha inandırıcı görünmesini sağlar.'
    },
    onboarding: {
      subtitle: 'Tek rehberli foto, yüzünde gerçekçi bir look.',
      eyebrow: 'Rehberli selfie',
      title: '1 foto, 3 otomatik kontrol',
      body: 'Hazır bir look ile başlarsın, render sonrası yoğunluğu ve paleti değiştirebilirsin. Motor dudak, yanak ve gözlerden önce yüzü, ışığı ve cildi okur.',
      selectedLook: 'Seçilen look',
      initialLook: 'Başlangıç looku',
      cta: 'Rehberli foto çek',
      privacy: 'Fotoğraf cihazında kalır: tüm işleme yerel olarak yapılır, hiçbir görüntü sunucuya gönderilmez.',
      demoTitle: 'Selfie olmadan dene',
      demoBody: 'Motoru hemen görmek için demo fotoğraf kullan.',
      checks: [
        { label: 'Eşit ışık', detail: 'Pencere önde, direkt güneş veya sert gölge yok.' },
        { label: 'Yüz karşıdan', detail: 'Kameraya bak, saçlar derli toplu ve yüzden uzak, sade arka plan.' },
        { label: 'Temiz cilt', detail: 'Ağız rahat, denenecek bölgelerde önceki makyaj az olsun.' }
      ]
    },
    captureHeader: { subtitle: 'Yüzünü kadraja al, sonra çek.' },
    access: { remaining: (count: number) => `${count} ücretsiz VTO denemesi kaldı`, ended: 'Ücretsiz denemeler bitti', note: 'Bir fotoğrafta oluşturulan her look bir deneme kullanır. Trial tam kullanımı açar.' },
    capture: {
      camera: { enable: 'Devam et', starting: 'Kamera açılıyor...', errorTitle: 'Hata', deniedTitle: 'Kamera engellendi', deniedBody: 'Tarayıcı ayarlarında erişime izin ver veya fotoğraf yükle.', ok: 'OK', snap: 'Fotoğraf çek', upload: '...veya fotoğraf yükle', privacy: 'Fotoğraf cihazında kalır. Sunucuya hiçbir şey gönderilmez.' },
      labels: { good: 'Temiz çekim', adjust: 'Neredeyse hazır', blocked: 'Düzelt', warming: 'Yüz kontrolü', unavailable: 'Kontrol yok', idle: 'Yüz kontrolü' },
      errors: {
        cameraUnavailable: 'Kamera kullanılamıyor',
        cameraUnsupported: 'Kamera bu tarayıcıda desteklenmiyor',
        videoNotReady: 'Kamera açıldı ama görüntü henüz hazır değil',
        captureFailed: 'Fotoğraf çekilemedi',
        unreadableImage: 'Görüntü okunamıyor',
        testFaceFailed: 'Test yüzü yüklenemedi'
      },
      scan: {
        startMessage: 'Kameraya dosdoğru bak.',
        startDetail: 'Işık, mesafe ve poz iyi olduğunda sana haber vereceğim.',
        centerFace: 'Yüzünü ortaya getir.',
        centerFaceDetail: 'Alın ve yanaklar açıkta, ışık eşit olsun.',
        fixBeforeShot: 'Sonuç hatalı olmasın diye çekimden önce bunu düzelt.',
        readyMessage: 'Şimdi çekebilirsin.',
        readyDetail: 'Yüz, mesafe ve ışık VTO için uygun.',
        improveMessage: 'Az kaldı: ışığı veya pozu iyileştir.',
        improveDetail: 'Çekebilirsin ama daha temiz bir fotoğraf daha iyi görünür.',
        unavailableMessage: 'Canlı kontrol kullanılamıyor.',
        unavailableDetail: 'Yine de çekebilirsin: tam kontrol daha sonra yapılacak.'
      }
    },
    paywall: {
      title: (limit: number) => `${limit} ücretsiz denemeyi kullandın`,
      body: 'Look denemeye devam etmek, Look Card’ı dışa aktarmak ve Virtual Try-On’u bu sınır olmadan kullanmak için BeautyIQ’yu aç.',
      loading: 'Ödeme sayfası açılıyor...',
      cta: 'Tam kullanımı aç',
      checkoutError: 'Ödeme şu anda kullanılamıyor. Birazdan tekrar dene.',
      privacy: 'Bu tarayıcıya özel anonim sınır. Uygulamak için selfie veya görüntü kaydetmiyoruz.'
    },
    noFace: {
      title: 'Yüz algılanamadı',
      fallback: 'Karşıdan çekilmiş, iyi aydınlatılmış, ağzı rahat ve neredeyse kapalı bir fotoğraf dene.'
    },
    result: {
      refineLook: 'Looku rötuşluyorum',
      viewLabel: 'Sonuç görünümü',
      compareLabel: 'Önce/sonra karşılaştırması',
      after: 'Sonra',
      before: 'Önce',
      compare: 'Karşılaştır',
      intensity: 'Makyaj yoğunluğu',
      hair: 'Saç',
      hairNone: 'Orijinal',
      hairForYou: 'Alt tonun için önerilen',
      hairNote: 'deneme koyu saçı pek açmaz',
      hairNoteDark: 'açık ya da gri saçta yansıma gibi görünür, koyulaştırmaz',
      subtone: 'Alt ton',
      warm: 'sıcak',
      cool: 'soğuk',
      neutral: 'nötr',
      opacityAdjusted: 'opaklık otomatik ayarlandı',
      light: 'Işık',
      lightAdjusted: 'yoğunluk selfie’ye göre kalibre edildi',
      skin: 'Cilt',
      skinAdjusted: 'pigmentler kalibre edildi',
      fallbackNotice: 'Sonuç görünür kalsın diye render’ı bu fotoğraf için optimize ettim.',
      quality: 'Kalite',
      engine: 'Motor',
      time: 'Süre',
      privacySummary: (engine: string) => `${engine}. Fotoğraf tarayıcıda kalır.`,
      simulationNote: 'Dikey geçiş, kirpik çizgisi netliği ve kaş yoğunluğu içeren simülasyon. Ton, cilt derinliği ve ışık opaklığı otomatik ayarlar. Nihai renk gerçek ışığa göre değişir: tonları mağazada kontrol et.',
      betterResult: 'Daha da hassas bir sonuç için',
      qualityWarningGeneric: 'Fotoğraf ideal değil: sonuç doğal kalsın diye yoğunluğu ve dokuyu yumuşattım.',
      premiumButton: 'Final render',
      premiumRendering: 'Final render oluşturuluyor...',
      premiumPrivacy: 'Final render, oluşturulan görüntüyü rötuş için geçici olarak BeautyIQ sunucusuna gönderir.',
      share: 'Looku paylaş',
      sharing: 'Görsel hazırlanıyor...',
      newPhoto: '↻ Yeni fotoğraf'
    },
    qualitySummary: {
      semanticHigh: 'Yüksek hassasiyet',
      semanticBalanced: 'Dengeli',
      evaluating: 'Değerlendiriliyor',
      excellent: 'Mükemmel',
      stable: 'Stabil',
      optimized: 'Optimize edildi',
      localMesh: 'Yerel yüz mesh’i',
      localMeshCachedParser: 'Yerel yüz mesh’i, önbellekli parser',
      advancedParsing: 'Gelişmiş yüz analizi'
    },
    skinDepth: { 'very-light': 'çok açık', light: 'açık', medium: 'orta', tan: 'buğday', deep: 'koyu' },
    skinChroma: { muted: 'soft', balanced: 'dengeli', rich: 'doygun' },
    components: { lipstick: 'Ruj', blush: 'Allık', eyeshadow: 'Far' },
    kit: { title: 'Kitin', notes: 'Renk notları', lipstick: 'Ruj', blush: 'Allık', eyeshadow: 'Far' },
    crossBrand: {
      title: 'Marka arası eşleşmeler',
      body: 'Birden fazla markadan eşdeğer tonlar, yalnızca gerçek renk eşleşmeleri (ΔE ≤ 5), yakınlığa göre sıralı.',
      buy: 'Satın al',
      viewLook: 'Bu looku gör',
      quality: { perfect: 'Mükemmel', excellent: 'Çok iyi', good: 'İyi', approximate: 'Yaklaşık' }
    },
    mirror: { title: 'Canlı ayna', hint: 'Yüzünü hareket ettir: makyaj seni takip eder. Sonuç ve eşleşmeler için Yakala.', capture: 'Yakala', searching: 'Yüzünü arıyorum…' },
    lookCard: {
      export: 'Dışa aktar',
      preparing: 'Hazırlanıyor...',
      code: 'Look kodu',
      temperature: 'Sıcaklık',
      skinFit: 'Cilt uyumu',
      counterBrief: 'Mağaza brifi',
      skin: 'Cilt',
      lips: 'Dudaklar',
      eyes: 'Gözler',
      cheeks: 'Yanaklar',
      avoid: 'Kaçın',
      controls: 'Render kontrolleri',
      canvasFooter: 'Bu kartı Kiko, Douglas, Sephora veya MAC’e götür ve eşdeğerini iste.'
    },
    rationale: {
      title: 'Neden işe yarıyor',
      forWho: 'Kimin için',
      when: 'Ne zaman',
      avoid: 'Kaçın',
      storeHint: 'Bu looku mağazaya götür. Makyaj uzmanın cildine uygun ürünleri bulmana yardımcı olur.'
    },
    beautyPlayers: {
      title: 'Looku şuraya götür',
      body: 'Look Card’ı göster ve palet, bitiş ve yoğunluk olarak eşdeğer ürünleri iste.'
    },
    premium: {
      progress: [
        'Final render hazırlanıyor...',
        'Sonuç premium motora gönderiliyor...',
        'Işık, cilt ve doku rötuşlanıyor...',
        'Nihai görünüm kontrol ediliyor...',
        'Render tamamlanıyor...'
      ],
      readyMs: (ms: number) => `Final render ${ms} ms’de hazır.`,
      ready: 'Final render hazır: sunucu rötuşu uygulandı.',
      localReady: 'Yerel render hazır: premium sunucu kullanılamıyor, sonuç korundu.',
      queued: 'Render sırada: premium sonucu bekliyorum...',
      unavailable: 'Final render şu anda kullanılamıyor. Yerel sonuç kullanılabilir durumda.',
      alt: (styleName: string) => `${styleName} BeautyIQ final render`,
      genericAlt: 'BeautyIQ final render'
    },
    quality: {
      blockers: {
        pose: 'Yüzünü kameraya biraz daha dönük tut: sonuç çok daha hassas olur.',
        mouth: 'Ağzını daha rahat ve neredeyse kapalı tut: dudak kenarları daha net çıkar.',
        distance: 'Yüzünü kameraya biraz yaklaştır: try-on daha detaylı olur.'
      },
      warnings: {
        tilted: 'Yüz hafif eğik: sonuç doğal kalsın diye göz ve yanak makyajını yumuşattım.',
        poseLimit: 'Poz biraz sınırda: inandırıcı kalması için look daha yumuşak tutuldu.',
        mouthOpen: 'Ağız hafif açık: taşma görünmesin diye ruju biraz yumuşattım.',
        faceFar: 'Yüz biraz uzak: hassasiyeti korumak için göz ve yanak makyajı daha hafif tutuldu.',
        lowLight: 'Işık yumuşak ya da az: daha temiz bir sonuç için renkleri daha soft tuttum.',
        unevenLight: 'Yüzündeki ışık eşit değil: doğal kalması için looku biraz daha hafif yaptım.',
        softPhoto: 'Fotoğraf biraz yumuşak: yoğunluk yerine hassasiyet ve doğallığı öne aldım.',
        notSharp: 'Fotoğraf tam net değil: look temiz kalacak şekilde optimize edildi.'
      }
    },
    lighting: {
      unmeasurable: 'Işık tam ölçülemedi: nötr bir kalibrasyon kullandım.',
      lowExposure: 'Fotoğraf az ışıklı: pigmenti hafifçe artırıp fazla parıltıyı azalttım.',
      highExposure: 'Fotoğraf çok parlak: yapıştırma efekti olmasın diye pigmenti ve parlamayı azalttım.',
      harshContrast: 'Yüzde güçlü kontrast var: yanakları, gözleri ve parlak bitişleri yumuşattım.'
    },
    skinTone: {
      unmeasurable: 'Cilt tonu ölçülemedi: nötr bir renk kalibrasyonu kullandım.',
      reducedSample: 'Cilt örneği küçük: renk kalibrasyonu temkinli kalıyor.'
    },
    loadDemoError: 'Demo fotoğraf yüklenemedi',
    detectionError: 'Yüz algılama başarısız'
  })
};

function isVtoLang(value: string | null | undefined): value is VtoLang {
  return Boolean(value && (VTO_LANGS as readonly string[]).includes(value));
}

export function resolveVtoLang(params?: URLSearchParams): VtoLang {
  const fromUrl = params?.get('lang');
  if (isVtoLang(fromUrl)) return fromUrl;

  try {
    const stored = JSON.parse(window.localStorage.getItem('beautyiq') || '{}');
    if (isVtoLang(stored.lang)) return stored.lang;
  } catch {
    // Ignore unreadable storage.
  }

  try {
    const legacy = window.localStorage.getItem('beautyiq_lang');
    if (isVtoLang(legacy)) return legacy;
  } catch {
    // Ignore unreadable storage.
  }

  // Nessuna scelta salvata → lingua del device (stessa logica di app.js).
  // Cruciale nel guscio nativo: localStorage parte vuoto su capacitor://.
  try {
    const cands = ([] as string[]).concat(navigator.languages ?? [], navigator.language ?? []);
    for (const c of cands) {
      const p = String(c || '').toLowerCase().slice(0, 2);
      if (isVtoLang(p)) return p;
    }
  } catch {
    // Ignore unreadable navigator.
  }

  return 'it';
}

// Copy for the CURRENT language, resolved with the same chain the app uses
// (?lang= → localStorage 'beautyiq'.lang → legacy 'beautyiq_lang' → 'it').
// For lib modules (capture, quality, lighting) that have no component context:
// they can call copyNow() at the moment they need a user-facing string, so the
// message follows the active language without threading `copy` through APIs.
export function copyNow(): VtoCopy {
  let params: URLSearchParams | undefined;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    // No window (or unreadable location): fall through to storage/default.
    params = undefined;
  }
  return VTO_COPY[resolveVtoLang(params)];
}
